import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Clock, Gamepad2, Package, Search } from 'lucide-react';
import { useSectionTranslation } from '@/hooks';
import { api, isApiError } from '@/lib/api';
import { headerTranslations } from './translations';

const RECENT_KEY = 'playerso:recent-searches';
const MAX_RECENT = 5;
const DEBOUNCE_MS = 300;

interface SearchAutocompleteProps {
  /** Wrapper classes — defaults to the desktop header layout. */
  className?: string;
  /**
   * `absolute` (default) overlays the results below the input; `inline`
   * renders them in flow so a height-animated parent (mobile menu) grows
   * naturally instead of clipping the dropdown; `overlay` renders a fixed
   * full-width panel (portaled to <body>) over a scrim — the standard
   * pattern for phone search, so the menu never jumps.
   */
  dropdownMode?: 'absolute' | 'inline' | 'overlay';
  /** Called after a navigation is triggered (e.g. to close the mobile menu). */
  onNavigate?: () => void;
  /** Large hero variant — taller field with a bigger icon, for the homepage. */
  size?: 'default' | 'large';
}

interface SearchItem {
  id: string;
  name: string;
  slug: string;
  type: 'product' | 'category' | 'recent';
  meta?: string;
  logo?: string | null;
}

interface SearchResponse {
  products: Array<{
    id: string;
    name: string;
    slug: string;
    lowestPrice: number | null;
    currency: string;
    logo?: string | null;
  }>;
  categories: Array<{ id: string; name: string; slug: string; type: string; logo?: string | null }>;
}

export function SearchAutocomplete({
  className,
  dropdownMode = 'absolute',
  onNavigate,
  size = 'default',
}: SearchAutocompleteProps = {}) {
  const t = useSectionTranslation(headerTranslations);
  const navigate = useNavigate();
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqRef = useRef(0);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const [recent, setRecent] = useState<string[]>([]);

  useEffect(() => {
    try {
      const s = localStorage.getItem(RECENT_KEY);
      if (s) setRecent(JSON.parse(s));
    } catch {}
  }, []);

  useEffect(
    () => () => {
      seqRef.current += 1;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const saveRecent = useCallback((q: string) => {
    setRecent((prev) => {
      const next = [q, ...prev.filter((s) => s !== q)].slice(0, MAX_RECENT);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const clearRecent = useCallback(() => {
    setRecent([]);
    try { localStorage.removeItem(RECENT_KEY); } catch {}
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      seqRef.current += 1;
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      const res = await api.get<SearchResponse>(
        `/marketplace/search?q=${encodeURIComponent(q)}&limit=6`,
      );
      if (seq !== seqRef.current) return;
      if (isApiError(res)) { setResults([]); setLoading(false); return; }
      const items: SearchItem[] = [
        ...(res.data?.categories ?? []).map((c) => ({ id: c.id, name: c.name, slug: c.slug, type: 'category' as const, meta: c.type, logo: c.logo })),
        ...(res.data?.products ?? []).map((p) => ({ id: p.id, name: p.name, slug: p.slug, type: 'product' as const, meta: p.lowestPrice != null ? `${p.lowestPrice} ${p.currency}` : undefined, logo: p.logo })),
      ];
      setResults(items);
      setLoading(false);
    }, DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      if (inputRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const submitSearch = useCallback(
    (value: string) => {
      const q = value.trim();
      if (!q) return;
      saveRecent(q);
      setOpen(false);
      setQuery('');
      setHi(-1);
      navigate(`/browse?q=${encodeURIComponent(q)}`);
      onNavigate?.();
    },
    [navigate, saveRecent, onNavigate],
  );

  const showRecent = open && query.trim().length < 2 && recent.length > 0;
  const showResults = open && query.trim().length >= 2;
  const allItems: SearchItem[] = showRecent
    ? recent.map((q) => ({ id: q, name: q, slug: '', type: 'recent' }))
    : results;
  const activeId = open && hi >= 0 && hi < allItems.length ? `${listboxId}-option-${hi}` : undefined;

  const go = useCallback((item: SearchItem) => {
    if (item.type === 'recent') { setQuery(item.name); inputRef.current?.focus(); return; }
    saveRecent(query.trim() || item.name);
    setOpen(false); setQuery('');
    navigate(item.type === 'category' ? `/category/${item.slug}` : `/product/${item.id}`);
    onNavigate?.();
  }, [navigate, query, saveRecent, onNavigate]);

  const onKey = useCallback((e: React.KeyboardEvent) => {
    const n = allItems.length;
    switch (e.key) {
      case 'ArrowDown':
        if (open && n) { e.preventDefault(); setHi((i) => (i + 1) % n); }
        break;
      case 'ArrowUp':
        if (open && n) { e.preventDefault(); setHi((i) => (i - 1 + n) % n); }
        break;
      case 'Enter':
        if (open && hi >= 0 && hi < n) {
          e.preventDefault();
          go(allItems[hi]);
        }
        break;
      case 'Escape':
        setOpen(false);
        setHi(-1);
        inputRef.current?.blur();
        break;
    }
  }, [open, allItems, hi, go]);

  const listbox =
    open && (showRecent || showResults) ? (
      <div
        ref={dropdownRef}
        id={listboxId}
        role="listbox"
        aria-label={t.search.placeholder}
        className={`${
          dropdownMode === 'overlay'
            ? 'fixed inset-x-2 top-[4.25rem] z-50 max-h-[calc(100dvh-6rem)] overflow-y-auto rounded-xl bg-surface-card border border-surface-border shadow-lift'
            : dropdownMode === 'inline'
              ? 'relative mt-1 inset-x-0 z-50 overflow-hidden rounded-xl bg-surface-card border border-surface-border shadow-lift'
              : 'absolute top-full mt-1 inset-x-0 z-50 max-h-80 overflow-y-auto rounded-xl bg-surface-card border border-surface-border shadow-lift'
        }`}
      >
        {showRecent && (
          <>
            <div className="flex items-center justify-between px-3 py-2 border-b border-surface-border">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">{t.search.recent}</span>
              <button type="button" onClick={clearRecent} className="text-[10px] text-brand-muted hover:text-accent transition-colors">{t.search.clearRecent}</button>
            </div>
            {recent.map((q, i) => (
              <button key={q} type="button" role="option" id={`${listboxId}-option-${i}`} aria-selected={hi === i}
                onClick={() => go({ id: q, name: q, slug: '', type: 'recent' })} onMouseEnter={() => setHi(i)}
                className={`w-full min-h-11 md:min-h-9 flex items-center gap-2.5 px-3 py-2 text-sm text-start transition-colors ${hi === i ? 'bg-surface-hover text-brand-white' : 'text-brand-muted hover:bg-surface-hover hover:text-brand-white'}`}>
                <Clock size={14} className="shrink-0 text-brand-muted/60" />
                <span className="truncate">{q}</span>
              </button>
            ))}
          </>
        )}
        {showResults && (
          <>
            {loading && results.length === 0 && (
              <p className="px-3 py-6 text-sm text-brand-muted text-center">{t.search.searching}</p>
            )}
            {results.length === 0 && !loading && <p className="px-3 py-6 text-sm text-brand-muted text-center">{t.search.noResults}</p>}
            {(() => {
              const cats = results.filter((r) => r.type === 'category');
              const prods = results.filter((r) => r.type === 'product');
              let idx = 0;
              return (
                <>
                  {cats.length > 0 && (
                    <>
                      <div className="px-3 pt-2.5 pb-1">
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">{t.search.categories}</span>
                      </div>
                      {cats.map((item) => { const i = idx++; return (
                        <button key={`c-${item.id}`} type="button" role="option" id={`${listboxId}-option-${i}`} aria-selected={hi === i}
                          onClick={() => go(item)} onMouseEnter={() => setHi(i)}
                          className={`w-full min-h-12 md:min-h-9 flex items-center gap-3 px-3 py-2 text-sm text-start transition-colors ${hi === i ? 'bg-surface-hover text-brand-white' : 'text-brand-muted hover:bg-surface-hover hover:text-brand-white'}`}>
                          <span className="h-9 w-9 md:h-8 md:w-8 shrink-0 rounded-md bg-surface-hover border border-surface-border flex items-center justify-center overflow-hidden">
                            {item.logo ? (
                              <img src={item.logo} alt="" className="h-full w-full object-cover" loading="lazy" />
                            ) : (
                              <Gamepad2 size={15} className="text-brand-muted/70" />
                            )}
                          </span>
                          <span className="truncate font-medium text-brand-white">{item.name}</span>
                          {item.meta && <span className="text-[10px] text-brand-muted/60 capitalize ms-auto">{item.meta}</span>}
                        </button>
                      ); })}
                    </>
                  )}
                  {prods.length > 0 && (
                    <>
                      <div className="px-3 pt-2.5 pb-1">
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-brand-muted">{t.search.products}</span>
                      </div>
                      {prods.map((item) => { const i = idx++; return (
                        <button key={`p-${item.id}`} type="button" role="option" id={`${listboxId}-option-${i}`} aria-selected={hi === i}
                          onClick={() => go(item)} onMouseEnter={() => setHi(i)}
                          className={`w-full min-h-12 md:min-h-9 flex items-center justify-between gap-3 px-3 py-2 text-sm text-start transition-colors ${hi === i ? 'bg-surface-hover text-brand-white' : 'text-brand-muted hover:bg-surface-hover hover:text-brand-white'}`}>
                          <span className="flex items-center gap-3 min-w-0">
                            <span className="h-9 w-9 md:h-8 md:w-8 shrink-0 rounded-md bg-surface-hover border border-surface-border flex items-center justify-center overflow-hidden">
                              {item.logo ? (
                                <img src={item.logo} alt="" className="h-full w-full object-cover" loading="lazy" />
                              ) : (
                                <Package size={15} className="text-brand-muted/70" />
                              )}
                            </span>
                            <span className="truncate">{item.name}</span>
                          </span>
                          {item.meta && <span className="text-xs text-accent shrink-0">{item.meta}</span>}
                        </button>
                      ); })}
                    </>
                  )}
                  {!loading && results.length > 0 && (
                    <button
                      type="button"
                      onClick={() => submitSearch(query)}
                      onMouseEnter={() => setHi(-1)}
                      className="w-full min-h-11 md:min-h-9 px-3 py-2 text-sm font-medium text-accent border-t border-surface-border transition-colors hover:bg-surface-hover"
                    >
                      {t.search.viewAll.replace('{query}', query.trim())}
                    </button>
                  )}
                </>
              );
            })()}
          </>
        )}
      </div>
    ) : null;

  const large = size === 'large';

  return (
    <div className={className ?? 'flex flex-1 lg:max-w-lg relative'}>
      <form
        role="search"
        className="relative w-full"
        onSubmit={(e) => {
          e.preventDefault();
          submitSearch(query);
        }}
      >
        <Search
          className={`absolute top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none ${large ? 'start-5' : 'start-[0.875rem]'}`}
          size={large ? 20 : 16}
        />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setHi(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={t.search.placeholder}
          aria-label={t.search.placeholder}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={activeId}
          autoComplete="off"
          role="combobox"
          className={
            large
              ? 'w-full h-14 rounded-2xl bg-surface-card border border-surface-border text-base text-brand-white placeholder:text-brand-muted focus:outline-none focus:border-accent/40 focus:shadow-accent-glow transition-all ps-14 pe-4'
              : 'w-full h-11 md:h-10 rounded-lg bg-surface-base md:bg-surface-card border border-surface-border text-sm text-brand-white placeholder:text-brand-muted focus:outline-none focus:border-accent/30 transition-colors ps-10 pe-3'
          }
        />
      </form>

      {listbox && dropdownMode === 'overlay'
        ? createPortal(
            <>
              <div className="fixed inset-0 z-40 bg-black/50" aria-hidden="true" />
              {listbox}
            </>,
            document.body,
          )
        : listbox}
    </div>
  );
}